(() => {
  const wishlistKey = 'kicksown:wishlist:v2';
  const wishlistVisitorKey = 'kicksown:wishlist:visitor:v1';
  const wishlistMigrationKey = 'kicksown:wishlist:shared-migrated:v1';
  const wishlistApiBase = '/apps/kicksown-wishlist/v1';
  const sharedCountCacheKey = 'kicksown:wishlist:counts:v1';
  const sharedCountCacheTtl = 5 * 60 * 1000;
  const readSharedCountCache = () => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(sharedCountCacheKey) || '{}');
      return new Map(Object.entries(cached));
    } catch (_) {
      return new Map();
    }
  };
  const sharedCountCache = readSharedCountCache();
  const sharedCountRequests = new Map();
  const pendingProductUpdates = new Set();
  let wishlistDelegationReady = false;

  const persistSharedCountCache = () => {
    try {
      sessionStorage.setItem(sharedCountCacheKey, JSON.stringify(Object.fromEntries(sharedCountCache)));
    } catch (_) {
      // Session storage may be unavailable in private browsing.
    }
  };

  const cacheSharedCount = (productId, count) => {
    sharedCountCache.set(String(productId), {
      count: Math.max(0, Number(count) || 0),
      updatedAt: Date.now()
    });
    persistSharedCountCache();
  };

  const visitorId = () => {
    try {
      let id = localStorage.getItem(wishlistVisitorKey);
      if (!id) {
        const random = window.crypto?.randomUUID?.().replaceAll('-', '') || `${Date.now()}_${Math.random().toString(36).slice(2)}`;
        id = `ks_${random}`;
        localStorage.setItem(wishlistVisitorKey, id);
      }
      return id;
    } catch (_) {
      return `ks_session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    }
  };

  const readWishlist = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(wishlistKey) || '[]');
      return Array.isArray(saved) ? saved.filter((item) => item && item.id) : [];
    } catch (_) {
      return [];
    }
  };

  const writeWishlist = (items) => {
    try {
      localStorage.setItem(wishlistKey, JSON.stringify(items));
    } catch (_) {
      // Local storage may be unavailable in private browsing.
    }
  };

  const productFromButton = (button) => ({
    id: String(button.dataset.productId),
    handle: button.dataset.productHandle || '',
    title: button.dataset.productTitle || '',
    url: button.dataset.productUrl || '#',
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || '',
    vendor: button.dataset.productVendor || ''
  });

  const updateWishlistCount = () => {
    const count = readWishlist().length;
    document.querySelectorAll('[data-ks-wishlist-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    });
  };

  const syncWishlistButtons = () => {
    const savedIds = new Set(readWishlist().map((item) => String(item.id)));
    document.querySelectorAll('[data-ks-wishlist]').forEach((button) => {
      const active = savedIds.has(String(button.dataset.productId));
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    });
  };

  const updateProductWishlistCount = (productId, count) => {
    document.querySelectorAll(`[data-ks-wishlist][data-product-id="${CSS.escape(String(productId))}"]`).forEach((button) => {
      const countElement = button.querySelector('[data-ks-product-wishlist-count]');
      if (!countElement) return;
      const minimum = Number(countElement.dataset.minCount || 1);
      countElement.textContent = String(Math.max(0, Number(count) || 0));
      countElement.hidden = Number(count) < minimum;
    });
  };

  const syncSharedWishlist = async (scope = document) => {
    const buttons = Array.from(scope.querySelectorAll('[data-ks-wishlist][data-product-id]'));
    const ids = [...new Set(buttons.map((button) => button.dataset.productId).filter(Boolean))];
    if (!ids.length) return;

    const now = Date.now();
    const missingIds = ids.filter((id) => {
      const cached = sharedCountCache.get(id);
      if (!cached || now - cached.updatedAt >= sharedCountCacheTtl) return true;
      updateProductWishlistCount(id, cached.count);
      return false;
    });

    for (let index = 0; index < missingIds.length; index += 50) {
      const batch = missingIds.slice(index, index + 50);
      const requestKey = [...batch].sort().join(',');
      try {
        let request = sharedCountRequests.get(requestKey);
        if (!request) {
          request = fetch(`${wishlistApiBase}/counts?ids=${encodeURIComponent(batch.join(','))}`, {
            credentials: 'same-origin'
          }).finally(() => sharedCountRequests.delete(requestKey));
          sharedCountRequests.set(requestKey, request);
        }
        const response = await request;
        if (!response.ok) continue;
        const data = await response.json();
        batch.forEach((id) => {
          const count = Number(data.counts?.[id] || 0);
          cacheSharedCount(id, count);
          updateProductWishlistCount(id, count);
        });
      } catch (_) {}
    }
  };

  const migrateLegacyWishlist = async () => {
    try {
      if (localStorage.getItem(wishlistMigrationKey) === 'true') return;
      for (const item of readWishlist()) {
        const response = await fetch(`${wishlistApiBase}/wishlist`, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ product_id: String(item.id), visitor_id: visitorId(), action: 'add' }) });
        if (!response.ok) return;
      }
      localStorage.setItem(wishlistMigrationKey, 'true');
    } catch (_) {}
  };

  const renderWishlistPage = () => {
    const page = document.querySelector('[data-ks-wishlist-page]');
    if (!page) return;
    const grid = page.querySelector('[data-ks-wishlist-grid]');
    const empty = page.querySelector('[data-ks-wishlist-empty]');
    const items = readWishlist();
    grid.innerHTML = '';

    items.forEach((item) => {
      const card = document.createElement('article');
      card.className = 'ks-wishlist-card';

      const imageLink = document.createElement('a');
      imageLink.className = 'ks-wishlist-card__image';
      imageLink.href = item.url;
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.title;
        image.loading = 'lazy';
        imageLink.appendChild(image);
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'ks-wishlist-card__remove';
      remove.setAttribute('aria-label', `Remove ${item.title} from wishlist`);
      const heart = document.querySelector('[data-ks-wishlist-link] svg')?.cloneNode(true);
      if (heart) remove.appendChild(heart);
      else remove.textContent = '♥';
      remove.addEventListener('click', () => {
        writeWishlist(readWishlist().filter((saved) => String(saved.id) !== String(item.id)));
        updateWishlistCount();
        syncWishlistButtons();
        renderWishlistPage();
      });

      const vendor = document.createElement('p');
      vendor.className = 'ks-wishlist-card__vendor';
      vendor.textContent = item.vendor || 'KICKSOWN';

      const title = document.createElement('a');
      title.className = 'ks-wishlist-card__title';
      title.href = item.url;
      title.textContent = item.title;

      const price = document.createElement('span');
      price.className = 'ks-wishlist-card__price';
      price.textContent = item.price;

      card.append(imageLink, remove, vendor, title, price);
      grid.appendChild(card);
    });

    grid.hidden = items.length === 0;
    empty.hidden = items.length !== 0;
  };

  const initWishlist = (scope = document) => {
    if (!wishlistDelegationReady) {
      wishlistDelegationReady = true;
      document.addEventListener('click', async (event) => {
        const button = event.target.closest('[data-ks-wishlist]');
        if (!button) return;

        event.preventDefault();
        event.stopPropagation();
        if (button.getAttribute('aria-busy') === 'true') return;

        const item = productFromButton(button);
        if (pendingProductUpdates.has(item.id)) return;
        pendingProductUpdates.add(item.id);
        const current = readWishlist();
        const exists = button.getAttribute('aria-pressed') === 'true';
        const optimisticSaved = !exists;
        const optimisticItems = optimisticSaved
          ? [...current.filter((saved) => String(saved.id) !== item.id), item]
          : current.filter((saved) => String(saved.id) !== item.id);

        writeWishlist(optimisticItems);
        updateWishlistCount();
        syncWishlistButtons();
        renderWishlistPage();
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch(`${wishlistApiBase}/wishlist`, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ product_id: item.id, visitor_id: visitorId(), action: exists ? 'remove' : 'add' }) });
          if (!response.ok) throw new Error('Wishlist update failed');
          const result = await response.json();
          const confirmedItems = result.saved
            ? [...current.filter((saved) => String(saved.id) !== item.id), item]
            : current.filter((saved) => String(saved.id) !== item.id);
          writeWishlist(confirmedItems);
          window.dataLayer?.push({ event: result.saved ? 'wishlist_add' : 'wishlist_remove', product_id: item.id, product_handle: item.handle, product_title: item.title });
          updateWishlistCount();
          syncWishlistButtons();
          cacheSharedCount(item.id, result.count);
          updateProductWishlistCount(item.id, result.count);
          renderWishlistPage();
        } catch (_) {
          writeWishlist(current);
          updateWishlistCount();
          syncWishlistButtons();
          renderWishlistPage();
          button.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-3px)' }, { transform: 'translateX(3px)' }, { transform: 'translateX(0)' }], { duration: 220 });
        } finally {
          pendingProductUpdates.delete(item.id);
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
        }
      });

      window.addEventListener('storage', (event) => {
        if (event.key !== wishlistKey) return;
        updateWishlistCount();
        syncWishlistButtons();
        renderWishlistPage();
      });
    }

    updateWishlistCount();
    syncWishlistButtons();
    renderWishlistPage();
    migrateLegacyWishlist().finally(() => syncSharedWishlist(scope));
  };

  const syncViewAll = (section, targetId) => {
    section.querySelectorAll('[data-ks-view-all]').forEach((link) => {
      link.hidden = link.dataset.target !== targetId;
    });
  };

  const initFeaturedViewAll = (scope = document) => {
    scope.querySelectorAll('[data-ks-featured-section]').forEach((section) => {
      if (section.dataset.ksFeaturedReady === 'true') return;
      section.dataset.ksFeaturedReady = 'true';
      const activeTab = section.querySelector('.tabs-nav__item[aria-expanded="true"]');
      if (activeTab) syncViewAll(section, activeTab.getAttribute('aria-controls'));

      section.querySelectorAll('.tabs-nav__item[aria-controls]').forEach((tab) => {
        tab.addEventListener('click', () => {
          syncViewAll(section, tab.getAttribute('aria-controls'));
          window.dataLayer?.push({
            event: 'featured_collection_tab_selected',
            collection_tab: tab.textContent.trim()
          });
        });
      });

      section.querySelectorAll('[data-ks-view-all]').forEach((link) => {
        link.addEventListener('click', () => {
          window.dataLayer?.push({
            event: 'featured_collection_view_all',
            destination_url: link.href
          });
        });
      });
    });
  };

  const addBusinessDays = (start, days) => {
    const date = new Date(start);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const weekday = date.getDay();
      if (weekday !== 0 && weekday !== 6) added += 1;
    }
    return date;
  };

  const shippingWindows = {
    AE: [8, 12],
    AT: [8, 12],
    AU: [8, 12],
    BE: [8, 12],
    BG: [8, 12],
    BR: [15, 25],
    CA: [8, 12],
    CH: [8, 12],
    CL: [10, 15],
    CO: [10, 15],
    CY: [10, 15],
    CZ: [8, 12],
    DE: [8, 12],
    DK: [8, 12],
    EE: [8, 12],
    ES: [8, 12],
    FI: [8, 12],
    FR: [8, 12],
    GB: [8, 12],
    GR: [8, 12],
    HR: [8, 12],
    HU: [8, 12],
    IE: [8, 12],
    IL: [8, 12],
    IT: [8, 12],
    JP: [8, 12],
    KR: [8, 12],
    TH: [8, 12],
    LT: [8, 12],
    LU: [8, 12],
    LV: [8, 12],
    MT: [10, 15],
    MX: [10, 15],
    NL: [8, 12],
    NO: [8, 12],
    PE: [12, 20],
    PL: [8, 12],
    PT: [8, 12],
    RO: [8, 12],
    RU: [12, 20],
    SA: [8, 12],
    SE: [8, 12],
    SI: [8, 12],
    SK: [8, 12],
    NZ: [8, 12],
    US: [8, 12],
    KW: [8, 12],
    QA: [8, 12],
    OM: [8, 12],
    BH: [8, 12]
  };

  const initShippingEstimators = (scope = document) => {
    scope.querySelectorAll('[data-ks-shipping-estimator]').forEach((estimator) => {
      if (estimator.dataset.ksShippingReady === 'true') return;
      estimator.dataset.ksShippingReady = 'true';

      const country = estimator.querySelector('[data-ks-shipping-country]');
      const postal = estimator.querySelector('[data-ks-shipping-postal]');
      const remote = estimator.querySelector('[data-ks-shipping-remote]');
      const result = estimator.querySelector('[data-ks-shipping-result]');
      if (!country || !postal || !remote || !result) return;

      const render = (trackInteraction = false) => {
        const isRemote = remote.checked;
        const [standardMinimum, standardMaximum] = shippingWindows[country.value] || [10, 18];
        const minimumDays = isRemote ? Math.max(standardMinimum + 4, 15) : standardMinimum;
        const maximumDays = isRemote ? Math.max(standardMaximum + 6, 21) : standardMaximum;
        const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
        const minimumDate = formatter.format(addBusinessDays(new Date(), minimumDays));
        const maximumDate = formatter.format(addBusinessDays(new Date(), maximumDays));
        const countryName = country.options[country.selectedIndex]?.text || 'your destination';
        const postalCode = postal.value.trim();
        const destination = postalCode ? `${countryName} ${postalCode}` : countryName;

        result.textContent = `Estimated delivery to ${destination}: ${minimumDate} – ${maximumDate} (${minimumDays}–${maximumDays} business days).`;

        if (trackInteraction) {
          window.dataLayer?.push({
            event: 'shipping_estimate_updated',
            destination_country: country.value,
            remote_area: isRemote,
            estimate_min_days: minimumDays,
            estimate_max_days: maximumDays
          });
        }
      };

      country.addEventListener('change', () => render(true));
      postal.addEventListener('change', () => render(true));
      remote.addEventListener('change', () => render(true));
      render();
    });
  };

  const initAll = (scope = document) => {
    initWishlist(scope);
    initFeaturedViewAll(scope);
    initShippingEstimators(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
